import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ImportanceLevel, getImportanceLabel } from "@/utils/weightedCompatibility";

interface PriorityBadgesProps {
  weights: any;
  firstName: string;
}

const preferenceLabels: Record<string, string> = {
  age: "Age",
  height: "Height",
  ethnicity: "Ethnicity",
  religiosity: "Religiosity",
  sect: "Sect",
  education: "Education",
  location: "Location",
  marital_status: "Marital Status",
  children: "Children",
  personality: "Personality",
  financial: "Financial Status",
  hijab: "Hijab",
  beard: "Beard",
  masjid_attendance: "Masjid Attendance",
};

export function PriorityBadges({ weights, firstName }: PriorityBadgesProps) {
  const topPriorities = Object.entries(weights)
    .filter(([, importance]) => importance === "very_important" || importance === "deal_breaker")
    .map(([key]) => key);

  const importantAreas = Object.entries(weights)
    .filter(([, importance]) => importance === "important")
    .map(([key]) => key);

  const flexibleAreas = Object.entries(weights)
    .filter(([, importance]) => importance === "flexible" || importance === "dont_care")
    .map(([key]) => key);

  if (topPriorities.length === 0 && importantAreas.length === 0 && flexibleAreas.length === 0) {
    return null;
  }

  return (
    <Card className="bg-muted/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">What matters most to {firstName}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {topPriorities.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-2">Top Priorities:</p>
            <div className="flex flex-wrap gap-2">
              {topPriorities.map((key) => {
                const importance = weights[key] as ImportanceLevel;
                return (
                  <Badge
                    key={key}
                    variant={importance === "deal_breaker" ? "destructive" : "default"}
                    className="text-xs"
                  >
                    {importance === "deal_breaker" ? "🔴" : "🟠"} {preferenceLabels[key] || key}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {importantAreas.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-2">Important:</p>
            <div className="flex flex-wrap gap-2">
              {importantAreas.map((key) => (
                <Badge key={key} variant="secondary" className="text-xs">
                  🟡 {preferenceLabels[key] || key}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {flexibleAreas.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-2">Flexible about:</p>
            <div className="flex flex-wrap gap-2">
              {flexibleAreas.map((key) => (
                <Badge key={key} variant="outline" className="text-xs">
                  🔵 {preferenceLabels[key] || key}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
